{
  "slug": "what-is-considered-a-high-iq",
  "title": "What is considered a high IQ?",
  "description": "Learn which IQ scores are commonly described as high, how they relate to percentiles, and why confidence intervals and test context matter.",
  "summary": "On a common mean-100, standard-deviation-15 scale, scores around 120 are well above average and scores around 130 are near the upper two percent. Labels vary by instrument and should not replace a full interpretation.",
  "category": "IQ score interpretation",
  "sections": [
    {
      "heading": "Common score and percentile reference points",
      "paragraphs": [
        "Modern IQ scores describe performance relative to an age-based norm group. On a commonly used scale, 100 is near the 50th percentile, 115 near the 84th, 120 near the 91st, and 130 near the 98th. These are approximate values from a normal model rather than universal cutoffs.",
        "Publishers and organizations use different descriptions. Terms such as above average, superior, very high, or gifted may have different boundaries, so the report and its technical manual take priority."
      ],
      "bullets": [
        "110–119: above the population mean",
        "120–129: well above average on many scales",
        "130 and above: approximately the upper two percent on a mean-100, SD-15 model"
      ]
    },
    {
      "heading": "A high composite can contain an uneven profile",
      "paragraphs": [
        "A total score combines selected abilities. Someone may perform especially strongly in verbal reasoning, visual reasoning, working memory, or processing speed while another area is closer to their age-group average.",
        "The profile, testing conditions, language, education, health, and reason for assessment can matter more than a single label. A high score does not prescribe personality, creativity, motivation, or future achievement."
      ]
    },
    {
      "heading": "Precision and responsible interpretation",
      "paragraphs": [
        "Every score has measurement uncertainty. Professional reports commonly include a confidence interval because a result of 129 is not meaningfully separated from 130 by a perfectly precise boundary.",
        "Use online results as indicative feedback. Decisions about education, clinical support, or eligibility should rely on an appropriate supervised instrument and qualified interpretation."
      ]
    }
  ],
  "faqs": [
    {
      "question": "Is an IQ of 120 considered high?",
      "answer": "It is around the 91st percentile on a mean-100, SD-15 model and is commonly described as well above average, but labels vary by test."
    },
    {
      "question": "Does a high IQ guarantee success?",
      "answer": "No. Achievement also depends on knowledge, motivation, health, personality, opportunity, persistence, and the environment."
    }
  ],
  "related": [
    "what-does-an-iq-of-120-mean",
    "iq-130-meaning",
    "iq-score-ranges-explained",
    "ceiling-and-floor-effects-in-iq-testing"
  ],
  "sources": [
    {
      "label": "APA Dictionary of Psychology — IQ",
      "url": "https://dictionary.apa.org/iq"
    },
    {
      "label": "Pearson — WAIS-5 sample score report",
      "url": "https://www.pearsonassessments.com/content/dam/school/global/clinical/us/assets/wais-5/wais-5-sample-score-report.pdf"
    }
  ]
}
